import queryString from 'query-string';
import { matchPath } from 'react-router-dom';

import { MESSAGE_TYPE } from '@constants/messages';
import { queryOptions } from '@constants/pagination';
import { generatePath } from '@helpers/route';

export const vsuiteRoutes = {
  ROOT: {
    get rule() {
      return '/';
    },
    get make() {
      return () => this.rule;
    },
  },
  DASHBOARD: {
    get rule() {
      return '/dashboard';
    },
    get make() {
      return () => this.rule;
    },
  },
  INVITATION: {
    get rule() {
      return '/invitation/:inviteToken';
    },
    get make() {
      return inviteToken => generatePath(this.rule, { inviteToken });
    },
  },
  LOGIN: {
    get rule() {
      return '/login';
    },
    get make() {
      return ({ returnTo } = {}) => {
        const pathname = window?.location?.pathname;
        const search = window?.location?.search;

        if (matchPath(pathname, { path: REDIRECT_EXCLUSIONS })) {
          return this.rule;
        }

        const defaultReturn = `${pathname}${search}`;
        const query = queryString.stringify({ returnTo: returnTo ?? defaultReturn }, queryOptions);

        return `${this.rule}${query ? `?${query}` : ''}`;
      };
    },
  },
  MESSAGES: {
    get rule() {
      return '/messages/:inbox(inbox|archive)/:category(filing|general|all)/:page(\\d+)/:threadId?';
    },
    get make() {
      return ({
        inbox = 'inbox',
        category = 'all',
        page = 1,
        threadId,
        dateFrom,
        dateTo,
        search,
        status,
        savedFilingPeriod,
      } = {}) => {
        const pathname = generatePath(this.rule, {
          inbox,
          category: savedFilingPeriod ? MESSAGE_TYPE.FILING : category,
          page,
          threadId,
        });
        const query = queryString.stringify(
          {
            dateFrom,
            dateTo,
            search,
            status,
            savedFilingPeriod,
          },
          queryOptions
        );

        return `${pathname}${query ? `?${query}` : ''}`;
      };
    },
  },
  MESSAGE_DETAIL: {
    get rule() {
      return '/messages/message/:threadId';
    },
    get make() {
      return threadId => generatePath(this.rule, { threadId });
    },
  },
  NOTES: {
    get rule() {
      return '/notes/:tab';
    },
    get make() {
      return tab => generatePath(this.rule, { tab });
    },
  },
  NOTES_PERIOD: {
    get rule() {
      return '/notes/:savedPeriodId/:tab';
    },
    get make() {
      return (savedPeriodId, tab) => generatePath(this.rule, { savedPeriodId, tab });
    },
  },
  PASSWORD_FORGOT: {
    get rule() {
      return '/forgot-password';
    },
    get make() {
      return () => this.rule;
    },
  },
  EMAIL_FORGOT: {
    get rule() {
      return '/forgot-email';
    },
    get make() {
      return () => this.rule;
    },
  },
  PASSWORD_LOST: {
    get rule() {
      return '/password-lost/:email/:token';
    },
    get make() {
      return (email, token) => generatePath(this.rule, { email, token });
    },
  },
  SIGN_UP: {
    get rule() {
      return '/sign-up';
    },
    get make() {
      return () => this.rule;
    },
  },
  TERMS_AND_CONDITIONS: {
    get rule() {
      return '/terms-and-conditions';
    },
    get make() {
      return () => this.rule;
    },
  },
  PRIVACY_POLICY: {
    get rule() {
      return '/privacy-policy';
    },
    get make() {
      return () => this.rule;
    },
  },
  TWO_FACTOR_AUTH: {
    get rule() {
      return '/2fa';
    },
    get make() {
      return stepId => generatePath(this.rule, { stepId });
    },
  },
  TWO_FACTOR_AUTH_SETUP: {
    get rule() {
      return '/2fa-setup/:stepId?';
    },
    get make() {
      return stepId => generatePath(this.rule, { stepId });
    },
  },
};

// List of routes excluded from "redirect back to" when logging in
const REDIRECT_EXCLUSIONS = [
  vsuiteRoutes.INVITATION.rule,
  vsuiteRoutes.LOGIN.rule,
  vsuiteRoutes.PASSWORD_FORGOT.rule,
  vsuiteRoutes.EMAIL_FORGOT.rule,
  vsuiteRoutes.PASSWORD_LOST.rule,
  vsuiteRoutes.SIGN_UP.rule,
  vsuiteRoutes.TWO_FACTOR_AUTH.rule,
  vsuiteRoutes.TWO_FACTOR_AUTH_SETUP.rule,
];
